import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformApi } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableIndexCell, TableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { Pagination } from '@/components/ui/pagination';
import { useClientPagination } from '@/hooks/useClientPagination';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageLoading } from '@/components/ui/loading';
import { FormError } from '@/components/ui/form-error';
import { formatDate } from '@/lib/utils';
import { Pencil, ShieldCheck, Eye, KeyRound, Ban, CheckCircle2, Copy, Download } from 'lucide-react';
import type { PlatformOrganization } from '@/lib/types';

export default function OrganizationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [supportOpen, setSupportOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [tempCreds, setTempCreds] = useState<{ organizationCode: string; adminEmail: string; adminPassword: string } | null>(null);

  const { data: org, isLoading } = useQuery({
    queryKey: ['platform', 'organization', id],
    queryFn: async () => {
      const r = await platformApi.get<{ success: boolean; data: PlatformOrganization }>(`/platform/organizations/${id}`);
      return r.data;
    },
    enabled: !!id,
  });

  const { data: users } = useQuery({
    queryKey: ['platform', 'organization', id, 'users'],
    queryFn: async () => {
      const r = await platformApi.get<{ success: boolean; data: any[] }>(`/platform/organizations/${id}/users`);
      return r.data ?? [];
    },
    enabled: !!id,
  });

  const suspendMutation = useMutation({
    mutationFn: () => platformApi.post(`/platform/organizations/${id}/suspend`),
    onSuccess: () => {
      addToast('Organization suspended. All active sessions were revoked.', 'success');
      queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
    onError: (err: any) => addToast(err.message, 'error'),
  });

  const activateMutation = useMutation({
    mutationFn: () => platformApi.post(`/platform/organizations/${id}/activate`),
    onSuccess: () => {
      addToast('Organization activated', 'success');
      queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
    onError: (err: any) => addToast(err.message, 'error'),
  });

  const resetMutation = useMutation({
    mutationFn: () => platformApi.post(`/platform/organizations/${id}/reset-admin-password`),
    onSuccess: (res: any) => {
      setTempCreds(res.credentials);
      addToast('Temporary password issued for the org admin', 'success');
    },
    onError: (err: any) => addToast(err.message, 'error'),
  });

  const { mutateAsync: enterSupport } = useMutation({
    mutationFn: async (r: string) => {
      const res = await platformApi.post<{ success: boolean; support: any }>('/platform/support/enter', { organizationId: id, reason: r });
      return res.support;
    },
    onError: (err: any) => addToast(err.message, 'error'),
  });

  const submitSupport = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      setReasonError('A reason (min 5 characters) is required.');
      const el = document.getElementById('support-reason');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLElement).focus({ preventScroll: true });
      }
      addToast('A reason (min 5 characters) is required', 'error');
      return;
    }
    setReasonError('');
    const support = await enterSupport(trimmed);
    if (support) {
      addToast(`Support mode started for ${support.organizationName}`, 'success');
      navigate('/super-admin/support');
    }
  };

  const { page, setPage, pageSize, total, pageItems } = useClientPagination(users ?? [], {
    resetKey: id,
  });

  if (isLoading) return <PageLoading />;
  if (!org) return <div className="text-secondary">Organization not found</div>;

  const copyCreds = async () => {
    if (!tempCreds) return;
    await navigator.clipboard.writeText(`Organization ID: ${tempCreds.organizationCode}\nAdmin Email: ${tempCreds.adminEmail}\nTemporary Password: ${tempCreds.adminPassword}`);
    addToast('Copied to clipboard', 'success');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-secondary mb-1">
            <Link to="/super-admin/organizations" className="hover:underline">Organizations</Link>
            <span>/</span><span className="font-mono">{org.organization_code}</span>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-heading font-bold text-primary">{org.name}</h1>
            <Badge variant={org.status === 'ACTIVE' ? 'approved' : 'rejected'}>{org.status}</Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/super-admin/organizations/${id}/edit`}><Button variant="outline" className="gap-2"><Pencil size={14} />Edit</Button></Link>
          {org.status === 'ACTIVE' ? (
            <Button variant="warning" className="gap-2" onClick={() => { if (confirm('Suspend this organization? All active sessions will be revoked.')) suspendMutation.mutate(); }} loading={suspendMutation.isPending}><Ban size={14} />Suspend</Button>
          ) : (
            <Button variant="success" className="gap-2" onClick={() => activateMutation.mutate()} loading={activateMutation.isPending}><CheckCircle2 size={14} />Activate</Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader><CardTitle className="text-base">Organization Details</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div><span className="text-secondary">Code</span><p className="font-mono font-medium">{org.organization_code}</p></div>
            <div><span className="text-secondary">Email</span><p className="font-medium break-words">{org.email || '—'}</p></div>
            <div><span className="text-secondary">Phone</span><p>{org.phone || '—'}</p></div>
            <div><span className="text-secondary">Address</span><p>{org.address || '—'}</p></div>
            <div><span className="text-secondary">Created</span><p>{formatDate(org.created_at)}</p></div>
            <div className="grid grid-cols-3 gap-2 pt-2">
              <div className="rounded-lg border border-default p-3 text-center"><p className="text-xl font-bold">{org.user_count ?? 0}</p><p className="text-xs text-secondary">Users</p></div>
              <div className="rounded-lg border border-default p-3 text-center"><p className="text-xl font-bold">{org.project_count ?? 0}</p><p className="text-xs text-secondary">Projects</p></div>
              <div className="rounded-lg border border-default p-3 text-center"><p className="text-xl font-bold">{org.requirement_count ?? 0}</p><p className="text-xs text-secondary">Reqs</p></div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Users</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableIndexHead /><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Roles</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {pageItems.map((u: any, index: number) => (
                  <TableRow key={u.id}>
                    <TableIndexCell value={rowSerial(index, page, pageSize)} />
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">{(u.roles ?? []).map((r: string) => <Badge key={r} variant="info">{r}</Badge>)}</div>
                    </TableCell>
                    <TableCell><Badge variant={u.is_active ? 'approved' : 'rejected'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                  </TableRow>
                ))}
                {(users ?? []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted">No users</TableCell></TableRow>}
              </TableBody>
            </Table>
            {(users ?? []).length > 0 && <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button className="gap-2" onClick={() => setSupportOpen(true)}><Eye size={14} />Enter Support Mode</Button>
        <Button variant="outline" className="gap-2" onClick={() => resetMutation.mutate()} loading={resetMutation.isPending}><KeyRound size={14} />Reset Admin Password</Button>
      </div>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} /> Enter Support Mode</DialogTitle>
            <DialogDescription>
              You are about to view <span className="font-medium text-primary">{org.name}</span> as support staff. A reason is required and all actions are audited. Support mode expires automatically after 30 minutes.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="reason">Reason *</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={e => { setReason(e.target.value); if (reasonError) setReasonError(''); }}
              placeholder="e.g. Investigating a reported issue with requirement approvals"
              rows={3}
              aria-invalid={reasonError ? true : undefined}
              aria-describedby={reasonError ? 'reason-error' : undefined}
              className={reasonError ? 'border-error' : undefined}
            />
            <FormError id="reason-error" message={reasonError || undefined} />
            <p className="text-xs text-muted mt-1">Read-only by default. Writes require explicit permission and are always audited.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupportOpen(false)}>Cancel</Button>
            <Button onClick={submitSupport} loading={resetMutation.isPending}><Eye size={14} /> Enter Support Mode</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempCreds} onOpenChange={(o) => { if (!o) setTempCreds(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> New Temporary Admin Password</DialogTitle>
            <DialogDescription>Shown once. The org admin must change it on their next login.</DialogDescription>
          </DialogHeader>
          {tempCreds && (
            <div className="rounded-lg border border-default bg-surface-secondary p-4 space-y-2 font-mono text-sm">
              <p><span className="text-secondary">Organization ID:</span> <span className="font-semibold">{tempCreds.organizationCode}</span></p>
              <p><span className="text-secondary">Admin Email:</span> <span className="font-semibold">{tempCreds.adminEmail}</span></p>
              <p><span className="text-secondary">Temporary Password:</span> <span className="font-semibold">{tempCreds.adminPassword}</span></p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={copyCreds}><Copy size={14} /> Copy</Button>
            <Button onClick={() => {
              const blob = new Blob([`Organization ID: ${tempCreds?.organizationCode}\nAdmin Email: ${tempCreds?.adminEmail}\nTemporary Password: ${tempCreds?.adminPassword}`], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `${tempCreds?.organizationCode}-admin-temp-password.txt`; a.click();
              URL.revokeObjectURL(url);
            }}><Download size={14} /> Download</Button>
            <Button onClick={() => setTempCreds(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
